/**
 * 현재 사용자 조회 훅 (TanStack Query)
 *
 * - GET /auth/me   현재 사용자(역할 포함) — 서버 권위 RBAC 출처
 *
 * 설계:
 * - 서버 응답(MeResponse)을 SessionUser 형태로 정규화.
 * - 신뢰 경계는 서버 — roles 는 UI 보조(메뉴 노출)일 뿐 실제 권한은 백엔드 재검증.
 * - 성공 시 user-store 에 동기화하여 AppShell/가드가 일관된 세션을 참조.
 *   (서버 데이터 캐시는 TanStack Query 가 단일 소유; store 동기화는 UI 전역 참조용.)
 * - UI 언어도 여기서 적용함 — 사용자 언어의 SSOT 는 DB(users.locale)이고 이 응답이 그 전달 경로임.
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { apiFetch, getActiveCompany } from "@/lib/api-client";
import type { SessionUser } from "@/lib/auth";
import { useApplyServerLang } from "@/lib/i18n/useServerLang";
import { queryKeys } from "@/lib/query-keys";
import { useCompanyStore, type CompanyMembership } from "@/store/company-store";
import { useUserStore } from "@/store/user-store";

/** GET /auth/me 응답 (계약서 1.3절) */
export interface MeResponse {
  /** 사용자 식별자 (Keycloak sub 또는 내부 id) */
  sub?: string;
  id?: string;
  email: string;
  name: string;
  /** RBAC 역할 목록 (서버 권위) */
  roles?: string[];
  /** 역할 ID (서버) */
  roleId?: string;
  /** 역할 표시 이름 (roles.name) — 표시는 roleLabel(roleCode, roleName) 로 번역해서 씀 */
  roleName?: string;
  /** 역할 코드 (roles.code, 예: "COMPANY_ADMIN") — 관리자 판단·표시 번역 기준. 사용자 역할이면 null */
  roleCode?: string | null;
  /** 직책명 (예: "품질책임자(QA)") — 헤더 사용자 메뉴 표시용(job_positions.name) */
  positionName?: string;
  /** 소속 회사(tenant) ID */
  companyId?: string;
  /** 소속 회사(tenant)명 (예: "브이웨이 메디칼") — 사이트 표시용(companies.name) */
  companyName?: string;
  /** 현재 활성 회사(tenant) slug */
  companySlug?: string;
  /** 사이트 관리자(회사 어드민) 여부 — 서버 권위 RBAC(시스템 설정 메뉴 게이트) */
  siteAdmin?: boolean;
  /** 슈퍼관리자(공급사) 여부 — 서버 권위 RBAC(모듈 프로비저닝 메뉴 게이트) */
  superAdmin?: boolean;
  /** 비밀번호 만료 여부(전역 보안정책) — 서버 권위. true 면 FE가 비밀번호 변경을 유도. */
  passwordExpired?: boolean;
  /** 소속 회사 목록(다회사 — 회사 스위처). companyId/roleName 등은 활성 회사 기준. */
  companies?: CompanyMembership[];
  /** 표시 언어(users.locale — ko/en/ja/zh). 사용자 언어의 서버 권위 출처. */
  locale?: string;
}

/** MeResponse → SessionUser 정규화 (sub 폴백: sub → id, roles 폴백: roleName) */
function toSessionUser(me: MeResponse): SessionUser {
  // 서버가 roles 배열을 주면 그대로, 없으면 roleId/roleName 단일값을 배열화(UX 보조).
  const roles =
    Array.isArray(me.roles) && me.roles.length > 0
      ? me.roles
      : [me.roleId, me.roleName].filter((r): r is string => Boolean(r));
  return {
    sub: me.sub ?? me.id ?? "",
    email: me.email,
    name: me.name,
    roles,
    roleName: me.roleName,
    roleCode: me.roleCode ?? undefined,
    positionName: me.positionName,
    companySlug: me.companySlug ?? "",
    isSiteAdmin: me.siteAdmin ?? undefined,
    isSuperAdmin: me.superAdmin ?? undefined,
    passwordExpired: me.passwordExpired ?? undefined,
  };
}

/**
 * 현재 사용자(/auth/me) 조회.
 * 성공 응답을 user-store 에 동기화함.
 *
 * @param enabled 인증 컨텍스트가 준비된 경우에만 호출(예: 토큰/모킹 세션 존재)
 */
export function useMe(enabled = true) {
  const setUser = useUserStore((s) => s.setUser);
  const setCompanies = useCompanyStore((s) => s.setCompanies);
  const setActiveCompanyId = useCompanyStore((s) => s.setActiveCompanyId);

  const query = useQuery({
    queryKey: queryKeys.me.current(),
    queryFn: () => apiFetch<MeResponse>("/api/auth/me"),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
  });

  // 서버 응답을 전역 세션 스토어 + 회사 스토어로 동기화 (UI 전역 참조용)
  useEffect(() => {
    if (query.data) {
      setUser(toSessionUser(query.data));
      setCompanies(query.data.companies ?? []);
      // 활성 회사 = 헤더로 선택한 값(있으면) 아니면 서버가 반환한 활성(=홈) 회사.
      setActiveCompanyId(getActiveCompany() ?? query.data.companyId ?? null);
    }
  }, [query.data, setUser, setCompanies, setActiveCompanyId]);

  // UI 언어 = 서버 저장값(users.locale). 적용 규칙은 공용 훅 한 곳에 둠.
  useApplyServerLang(query.data?.locale);

  return query;
}
